import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  BarChart3,
  TrendingUp,
  Users,
  Eye,
  Clock,
  Calendar,
  Download,
  ThumbsUp,
  MessageSquare,
  Share2,
  Activity,
} from 'lucide-react';
import { format, subDays, subMonths } from 'date-fns';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

type DateRange = '7d' | '30d' | '90d' | 'all';

interface AnalyticsSummary {
  totalStreams: number;
  totalViews: number;
  totalWatchTime: number;
  avgEngagement: number;
  completedStreams: number;
  scheduledStreams: number;
  liveStreams: number;
  avgDuration: number;
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  topPlatform: string | null;
  avgWatchTime: number;
}

interface PlatformComparison {
  platform: string;
  streams: number;
  totalViews: number;
  totalWatchTime: number;
  avgEngagement: number;
  avgDuration: number;
  avgWatchTime: number;
}

interface TrendData {
  date: string;
  views: number;
  watchTime: number;
  engagement: number;
  streams: number;
}

export default function AnalyticsEnhancedPage() {
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');

  // Calculate date range
  const getDateRange = () => {
    const today = new Date();
    const dateMap: Record<DateRange, { from: string; to: string } | null> = {
      '7d': {
        from: format(subDays(today, 7), 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
      },
      '30d': {
        from: format(subDays(today, 30), 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
      },
      '90d': {
        from: format(subMonths(today, 3), 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
      },
      'all': null,
    };
    return dateMap[dateRange];
  };

  const dateParams = getDateRange();

  // Fetch analytics summary
  const { data: summary, isLoading: summaryLoading } = useQuery<AnalyticsSummary>({
    queryKey: ['/api/analytics/summary', dateParams?.from, dateParams?.to],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateParams?.from) params.set('dateFrom', dateParams.from);
      if (dateParams?.to) params.set('dateTo', dateParams.to);
      
      const res = await fetch(`/api/analytics/summary?${params}`);
      if (!res.ok) throw new Error('Failed to fetch summary');
      return res.json();
    },
  });

  // Fetch platform comparison data
  const { data: platformData = [] } = useQuery<PlatformComparison[]>({
    queryKey: ['/api/analytics/comparative', dateParams?.from, dateParams?.to],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateParams?.from) params.set('dateFrom', dateParams.from);
      if (dateParams?.to) params.set('dateTo', dateParams.to);
      
      const res = await fetch(`/api/analytics/comparative?${params}`);
      if (!res.ok) throw new Error('Failed to fetch comparison data');
      return res.json();
    },
  });

  // Fetch trend data
  const { data: trendData = [] } = useQuery<TrendData[]>({
    queryKey: [
      '/api/analytics/trends',
      dateParams?.from,
      dateParams?.to,
      selectedPlatform === 'all' ? null : selectedPlatform,
    ],
    queryFn: async () => {
      if (!dateParams) {
        // For "all time", use a reasonable default range
        const today = new Date();
        const from = format(subMonths(today, 6), 'yyyy-MM-dd');
        const to = format(today, 'yyyy-MM-dd');
        const params = new URLSearchParams({ dateFrom: from, dateTo: to });
        if (selectedPlatform !== 'all') params.set('platform', selectedPlatform);
        
        const res = await fetch(`/api/analytics/trends?${params}`);
        if (!res.ok) throw new Error('Failed to fetch trends');
        return res.json();
      }

      const params = new URLSearchParams({
        dateFrom: dateParams.from,
        dateTo: dateParams.to,
      });
      if (selectedPlatform !== 'all') params.set('platform', selectedPlatform);
      
      const res = await fetch(`/api/analytics/trends?${params}`);
      if (!res.ok) throw new Error('Failed to fetch trends');
      return res.json();
    },
    enabled: !!dateParams || dateRange === 'all',
  });

  // Export CSV handler
  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (dateParams?.from) params.set('dateFrom', dateParams.from);
      if (dateParams?.to) params.set('dateTo', dateParams.to);
      
      const res = await fetch(`/api/analytics/export/csv?${params}`);
      if (!res.ok) throw new Error('Failed to export CSV');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `streampilot-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  if (summaryLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                <div className="h-4 w-4 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Track your streaming performance across all platforms
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={dateRange} onValueChange={(value) => setDateRange(value as DateRange)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExportCSV} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Streams</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalStreams || 0}</div>
            <p className="text-xs text-muted-foreground">
              {summary?.completedStreams || 0} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalViews?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">
              {summary?.totalStreams ? Math.round(summary.totalViews / summary.totalStreams) : 0} avg per stream
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Watch Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary?.totalWatchTime ? Math.round(summary.totalWatchTime / 60) : 0}h
            </div>
            <p className="text-xs text-muted-foreground">
              {summary?.avgWatchTime || 0} min avg
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Engagement Rate</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary?.avgEngagement ? Number(summary.avgEngagement).toFixed(2) : '0.00'}%
            </div>
            <p className="text-xs text-muted-foreground">
              Average across all streams
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Engagement Metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Likes</CardTitle>
            <ThumbsUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalLikes?.toLocaleString() || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Comments</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalComments?.toLocaleString() || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Shares</CardTitle>
            <Share2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalShares?.toLocaleString() || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <Tabs defaultValue="trends" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trends">Historical Trends</TabsTrigger>
          <TabsTrigger value="platforms">Platform Comparison</TabsTrigger>
          <TabsTrigger value="distribution">Distribution</TabsTrigger>
        </TabsList>

        {/* Historical Trends */}
        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Performance Over Time</CardTitle>
                  <CardDescription>
                    Track views, watch time, and engagement over time
                  </CardDescription>
                </div>
                <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Platforms</SelectItem>
                    {platformData.map((p) => (
                      <SelectItem key={p.platform} value={p.platform} className="capitalize">
                        {p.platform}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => format(new Date(value), 'MMM d')}
                    />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip 
                      labelFormatter={(value) => format(new Date(value as string), 'MMM d, yyyy')}
                    />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="views"
                      stroke="#8884d8"
                      strokeWidth={2}
                      name="Views"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="engagement"
                      stroke="#82ca9d"
                      strokeWidth={2}
                      name="Engagement %"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  No trend data available for the selected period
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Platform Comparison */}
        <TabsContent value="platforms" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Platform Performance Comparison</CardTitle>
              <CardDescription>
                Compare metrics across YouTube, Twitch, and Facebook
              </CardDescription>
            </CardHeader>
            <CardContent>
              {platformData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={platformData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="platform" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="totalViews" fill="#8884d8" name="Total Views" />
                    <Bar dataKey="streams" fill="#82ca9d" name="Streams" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  No platform data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Platform Stats Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Platform Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-4">Platform</th>
                      <th className="text-right py-2 px-4">Streams</th>
                      <th className="text-right py-2 px-4">Views</th>
                      <th className="text-right py-2 px-4">Avg Watch Time</th>
                      <th className="text-right py-2 px-4">Engagement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {platformData.map((platform) => (
                      <tr key={platform.platform} className="border-b">
                        <td className="py-2 px-4">
                          <Badge variant="outline" className="capitalize">
                            {platform.platform}
                          </Badge>
                        </td>
                        <td className="text-right py-2 px-4">{platform.streams}</td>
                        <td className="text-right py-2 px-4">{platform.totalViews.toLocaleString()}</td>
                        <td className="text-right py-2 px-4">{platform.avgWatchTime} min</td>
                        <td className="text-right py-2 px-4">
                          {Number(platform.avgEngagement).toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Distribution Charts */}
        <TabsContent value="distribution" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Views by Platform</CardTitle>
              </CardHeader>
              <CardContent>
                {platformData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={platformData}
                        dataKey="totalViews"
                        nameKey="platform"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(entry) => `${entry.platform}: ${entry.totalViews}`}
                      >
                        {platformData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Stream Count by Platform</CardTitle>
              </CardHeader>
              <CardContent>
                {platformData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={platformData}
                        dataKey="streams"
                        nameKey="platform"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(entry) => `${entry.platform}: ${entry.streams}`}
                      >
                        {platformData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Platform API Integration Notice */}
      {(summary?.totalViews === 0 || !summary?.totalViews) && (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle>🚀 Platform API Integration Coming Soon</CardTitle>
            <CardDescription>
              Currently showing stream count and scheduling data. Connect your platform APIs to
              unlock detailed analytics:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>YouTube Analytics API - Views, watch time, engagement metrics</li>
              <li>Twitch Analytics API - Concurrent viewers, chat activity</li>
              <li>Facebook Insights API - Reactions, shares, comments</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4">
              For now, analytics data can be manually updated via the streams API or will be
              automatically collected once platform integrations are enabled.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
